import { Box } from "@mui/material";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Loader } from "../../../common/components/loader";
import DashboardLayout from "../../../common/layouts/dashboard";
import { useAppDispatch } from "../../../core/hooks/useStore";
import { RootState } from "../../../core/store/store";
import { homeSlice } from "../../home";
import { KeywordPigCreateDialog } from "./keyword-pig-create-dialog";
import KeywordPigDesc from "./keyword-pig-desc";
import KeywordPigHeader from "./keyword-pig-header";
import KeywordPigLoading from "./keyword-pig-loading";
import KeywordPigLocked from "./keyword-pig-locked";
import toolsSlice from "./keyword-pig.slice";
import KeywordPigResults from "./results/results";
import useTool from "./use-keyword-pig";

const KeywordPig = () => {
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const toolsState = useSelector((state: RootState) => state.keywordPig);
  const navigate = useNavigate();
  const { resultId } = useParams<{ resultId?: string }>();
  const hasAccess = auth.tools?.keywordTool;
  const loadingRef = useRef(false);
  
  const tool = useTool();

  useEffect(() => {
    dispatch(homeSlice.actions.setActiveMenu("keyword-tool"));
    dispatch(toolsSlice.actions.setType("KEYWORD"));
    
    if (!hasAccess) {
      navigate("/tools");
      return;
    }

    if (resultId && !loadingRef.current) {
      loadingRef.current = true;
      dispatch(toolsSlice.actions.setStatus("loading"));
      
      tool.getJob(resultId).then(result => {
        if (result && result.finishedAt) {
          dispatch(toolsSlice.actions.setData(result));
          dispatch(toolsSlice.actions.setStatus("created"));
        } else {
          dispatch(toolsSlice.actions.setStatus("initialized"));
          navigate("/tools/keyword-tool");
        }
        loadingRef.current = false;
      }).catch(error => {
        console.error("Failed to load result:", error);
        dispatch(toolsSlice.actions.setStatus("initialized"));
        navigate("/tools/keyword-tool");
        loadingRef.current = false;
      });
    } else if (!resultId) {
      dispatch(toolsSlice.actions.setStatus("initialized"));
    }
  }, [resultId, hasAccess]);

  const KeywordPigLoader = () => {
    return <Loader />;
  };

  const handleOpen = () => {
    dispatch(toolsSlice.actions.setCreateDialogOpened(true));
  };

  const handleClose = () => {
    dispatch(toolsSlice.actions.setCreateDialogOpened(false));
  };

  return (
    <DashboardLayout active={"keyword-pig"} name="Keyword Pig Pro">
      <Box>
        {toolsState.tool.status === "loading" && <KeywordPigLoader />}
        {toolsState.tool.status === "initialized" && (
          <>
            <KeywordPigHeader />
            <KeywordPigDesc />
            <KeywordPigCreateDialog
              onClose={handleClose}
              open={toolsState.tool.createDialogOpened}
              defaultNiche={toolsState.tool.defaultSearch}
            />
          </>
        )}
        {toolsState.tool.status === "unauthorized" && <KeywordPigLocked />}
        {toolsState.tool.status === "creating" && (
          <>
            <KeywordPigHeader />
            <KeywordPigLoading />
          </>
        )}
        {toolsState.tool.status === "created" && (
          <>
            <KeywordPigHeader />
            <KeywordPigResults />
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default KeywordPig;
